/**
 * Expense Report Page (T165, T166)
 * Expenses grouped by category with subtotals, pie chart, and CSV export
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Download, ArrowLeft, DollarSign, TrendingDown, Receipt } from 'lucide-react';
import { useExpenseReport, useExportExpenses } from '../hooks/useExpenses';

// Colors for pie chart segments
const CHART_COLORS = [
  '#f97316',
  '#3b82f6',
  '#22c55e',
  '#eab308',
  '#a855f7',
  '#ec4899',
  '#14b8a6',
  '#f43f5e',
  '#6366f1',
  '#84cc16',
  '#06b6d4',
  '#d946ef',
];

export default function ExpenseReportPage() {
  const navigate = useNavigate();
  const exportExpenses = useExportExpenses();

  // Default to current month
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .split('T')[0];

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(lastOfMonth);

  const { data: report, isLoading } = useExpenseReport(dateFrom, dateTo);

  const handleExport = (format: 'csv' | 'pdf') => {
    exportExpenses.mutate({ dateFrom, dateTo, format });
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/expenses')}
            className="claude-button claude-button-ghost p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-1">
            <h1>Expense Report</h1>
            <p className="text-muted-foreground">Spending breakdown by category</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exportExpenses.isPending}
            className="claude-button claude-button-ghost"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Date Range */}
      <div className="claude-card p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium whitespace-nowrap">From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium whitespace-nowrap">To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Quick ranges */}
          <div className="flex gap-1 ml-auto">
            {[
              { label: 'This Month', from: firstOfMonth, to: lastOfMonth },
              {
                label: 'Last Month',
                from: new Date(now.getFullYear(), now.getMonth() - 1, 1)
                  .toISOString()
                  .split('T')[0],
                to: new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0],
              },
              {
                label: 'This Year',
                from: `${now.getFullYear()}-01-01`,
                to: `${now.getFullYear()}-12-31`,
              },
            ].map((range) => (
              <button
                key={range.label}
                onClick={() => {
                  setDateFrom(range.from);
                  setDateTo(range.to);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  dateFrom === range.from && dateTo === range.to
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="claude-spinner mx-auto"></div>
        </div>
      )}

      {/* Report Content */}
      {!isLoading && report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="claude-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-destructive" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Spent</p>
                  <p className="text-2xl font-semibold tracking-tight mt-1">
                    {report.grandTotalFormatted}
                  </p>
                </div>
              </div>
            </div>

            <div className="claude-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-semibold tracking-tight mt-1">{report.count}</p>
                </div>
              </div>
            </div>

            <div className="claude-card p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingDown className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Average</p>
                  <p className="text-2xl font-semibold tracking-tight mt-1">
                    {report.count > 0
                      ? `$${(report.grandTotal / report.count).toFixed(2)}`
                      : '$0.00'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Category Breakdown with Pie Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart (CSS-based) */}
            <div className="claude-card p-6">
              <h3 className="font-semibold mb-4">Spending by Category</h3>
              {report.byCategory.length > 0 ? (
                <div className="flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      {(() => {
                        let cumulative = 0;
                        return report.byCategory.map((cat, i) => {
                          const pct = cat.percentage;
                          const offset = cumulative;
                          cumulative += pct;
                          const dashArray = `${pct} ${100 - pct}`;
                          return (
                            <circle
                              key={cat.category}
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke={CHART_COLORS[i % CHART_COLORS.length]}
                              strokeWidth="20"
                              strokeDasharray={dashArray}
                              strokeDashoffset={-offset}
                              pathLength="100"
                            />
                          );
                        });
                      })()}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-xl font-bold">{report.grandTotalFormatted}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No expense data for this period
                </p>
              )}

              {/* Legend */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                {report.byCategory.map((cat, i) => (
                  <div key={cat.category} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                    />
                    <span className="text-xs text-muted-foreground truncate">
                      {cat.categoryLabel} ({cat.percentage}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Table */}
            <div className="claude-card overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="font-semibold">Category Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        Category
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        Count
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        Total
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {report.byCategory.map((cat, i) => (
                      <tr key={cat.category} className="hover:bg-muted/20">
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                              }}
                            />
                            {cat.categoryLabel}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-muted-foreground">
                          {cat.count}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {cat.totalFormatted}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-muted-foreground">
                          {cat.percentage}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/30">
                      <td className="px-4 py-3 text-sm font-semibold">Total</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{report.count}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">
                        {report.grandTotalFormatted}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Top Vendors */}
          {report.topVendors.length > 0 && (
            <div className="claude-card overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="font-semibold">Top Vendors</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        Vendor
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        Expenses
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">
                        Total Spent
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {report.topVendors.map((v) => (
                      <tr key={v.vendor} className="hover:bg-muted/20">
                        <td className="px-4 py-3 text-sm font-medium">{v.vendor}</td>
                        <td className="px-4 py-3 text-sm text-right text-muted-foreground">
                          {v.count}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {v.totalFormatted}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Monthly Trend */}
          {report.byMonth.length > 1 && (
            <div className="claude-card p-6">
              <h3 className="font-semibold mb-4">Monthly Trend</h3>
              <div className="flex items-end gap-2 h-32">
                {report.byMonth.map((m) => {
                  const maxTotal = Math.max(...report.byMonth.map((x) => x.total));
                  const height = maxTotal > 0 ? (m.total / maxTotal) * 100 : 0;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-muted-foreground">{m.totalFormatted}</span>
                      <div
                        className="w-full bg-primary/80 rounded-t-sm transition-all"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      <span className="text-xs text-muted-foreground">{m.month.split('-')[1]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {!isLoading && report && report.count === 0 && (
        <div className="text-center py-12">
          <BarChart3 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-1">No expenses in this period</h3>
          <p className="text-muted-foreground text-sm">
            Try adjusting the date range or add some expenses first
          </p>
        </div>
      )}
    </div>
  );
}
